/**
 * Data mappers for converting GraphQL Rental types to legacy interfaces
 *
 * These mappers ensure compatibility between the new GraphQL data structures
 * and existing component interfaces, allowing gradual migration.
 */

import type {
  IRental,
  IRentalBilling,
  IRate,
  IPerk,
  IFee,
  IPendingCharge,
  IRentalSupport,
} from '../../../../ts/interface/rental.interface';
import type { ICoupon } from '../../../../ts/interface/coupon.interface';
import type { RentalGraphQL } from './useRentalsRelay';
import type {
  RentalDetailGraphQL,
  RateGraphQL,
  PerkGraphQL,
  CouponGraphQL,
  FeeGraphQL,
  SupportMessageGraphQL,
} from './useRentalDetailRelay';

// ============================================================================
// Rental List Mappers
// ============================================================================

/**
 * Map a GraphQL Rental (from list) to the legacy IRental interface
 *
 * @param rental - The GraphQL rental data from list query
 * @returns IRental compatible object (partial - list queries don't include all fields)
 */
export function mapRelayRentalToIRental(rental: RentalGraphQL): Partial<IRental> {
  return {
    id: rental.id, // Use GraphQL UUID for routing
    gid: rental.id,
    old_id: rental.oldId || '',
    startTime: rental.startTime || '',
    endTime: rental.endTime || null,
    duration: rental.duration || 0,
    end_by: rental.endBy || undefined,
    chargeAmount: rental.chargeAmount || 0,
    rating: rental.rating || 0,

    // User info
    user: rental.user
      ? {
          full_name:
            rental.user.fullName || `${rental.user.firstName} ${rental.user.lastName}`,
          phone_number: rental.user.phone,
          first_name: rental.user.firstName,
          last_name: rental.user.lastName,
        }
      : { full_name: '', phone_number: '', first_name: '', last_name: '' },

    // Hardware info
    hardware: rental.hardware
      ? ({
          id: rental.hardware.oldId || rental.hardware.id,
          old_id: rental.hardware.oldId || '',
          number: rental.hardware.number || 0,
          type_name: rental.hardware.hardwareType?.name || '',
        } as any)
      : ({} as any),
    hardwareNumber: rental.hardware?.number || 0,

    // Station info
    start: {
      station: rental.startLocation
        ? {
            id: rental.startLocation.oldId || rental.startLocation.id,
            name: rental.startLocation.name,
            longitude: 0,
            latitude: 0,
          }
        : { id: '', name: '', longitude: 0, latitude: 0 },
      time: rental.startTime || '',
      triggered_by_type: '',
    },

    end: rental.endTime
      ? {
          station: rental.endLocation
            ? {
                id: rental.endLocation.oldId || rental.endLocation.id,
                name: rental.endLocation.name,
                longitude: 0,
                latitude: 0,
              }
            : { id: '', name: '', longitude: 0, latitude: 0 },
          time: rental.endTime,
          triggered_by_type: '',
        }
      : undefined,

    // System info
    system: rental.system
      ? ({
          id: rental.system.oldId || rental.system.id,
          gid: rental.system.id,
          old_id: rental.system.oldId || '',
          name: rental.system.name,
        } as any)
      : undefined,

    // Billing - minimal for list view
    billing: {
      total: rental.chargeTotal || rental.chargeAmount || 0,
      currency: 'USD',
      transactions: [],
      parking_credit: 0,
      usage_sub_total: 0,
      admin_modification: 0,
      admin_modification_reason: '',
      sub_total: rental.chargeTotal || rental.chargeAmount || 0,
      tax_rate: 0,
      tax_amount: 0,
      void_charge: false,
      void_by: { full_name: '', email: '' },
      voided_on: '',
      void_reason: '',
      pending_charge: { pending: false, note: '', decision_date: '', status: false },
      rate: {} as IRate,
      perk: {} as IPerk,
      fees: [],
      coupon: {} as ICoupon,
    },
  };
}

/**
 * Map an array of GraphQL rentals to IRental array
 */
export function mapRelayRentalsToIRentals(rentals: RentalGraphQL[]): Partial<IRental>[] {
  return rentals.map(mapRelayRentalToIRental);
}

// ============================================================================
// Rental Detail Mappers
// ============================================================================

/**
 * Map a full GraphQL Rental detail to the legacy IRental interface
 *
 * @param rental - The GraphQL rental detail data
 * @returns Full IRental compatible object
 */
export function mapRelayRentalDetailToIRental(rental: RentalDetailGraphQL): IRental {
  const rate = rental.rate ? mapRelayRateToIRate(rental.rate) : ({} as IRate);
  const perk = rental.perk ? mapRelayPerkToIPerk(rental.perk) : ({} as IPerk);
  const coupon = rental.coupon ? mapRelayCouponToICoupon(rental.coupon) : ({} as ICoupon);
  const fees = rental.fees ? mapRelayFeesToIFees(rental.fees as FeeGraphQL[]) : [];

  const billing: IRentalBilling = {
    total: rental.chargeTotal || rental.chargeAmount || 0,
    currency: 'USD',
    // Charge/hold/wallet details are fetched via GraphQL by-id hooks in the
    // detail page, keyed off rental.charge / rental.ccHold / rental.walletTransaction.
    transactions: [],
    parking_credit: rental.parkingCredit || 0,
    usage_sub_total: rental.usageSubtotal || 0,
    admin_modification: rental.adminModification || 0,
    admin_modification_reason: rental.adminModificationReason || '',
    sub_total: rental.usageSubtotal || 0,
    tax_rate: rental.taxRate ? parseFloat(rental.taxRate) : 0,
    tax_amount: rental.taxAmount ? parseFloat(rental.taxAmount) : 0,
    void_charge: rental.isVoidCharge || false,
    void_by: {
      full_name: rental.voidByAdmin
        ? `${rental.voidByAdmin.firstName || ''} ${
            rental.voidByAdmin.lastName || ''
          }`.trim()
        : '',
      email: rental.voidByAdmin?.email || '',
    },
    voided_on: rental.voidedOn || '',
    void_reason: rental.voidReason || '',
    pending_charge: mapRelayPendingCharge(rental),
    rate,
    perk,
    fees,
    coupon,
  };

  // Parse triggered by JSON strings
  let startTriggeredByType = '';
  let endTriggeredByType = '';
  try {
    if (rental.startTriggeredBy) {
      const parsed = JSON.parse(rental.startTriggeredBy);
      startTriggeredByType = parsed.type || '';
    }
  } catch {
    startTriggeredByType = rental.startType || '';
  }
  try {
    if (rental.endTriggeredBy) {
      const parsed = JSON.parse(rental.endTriggeredBy);
      endTriggeredByType = parsed.type || '';
    }
  } catch {
    endTriggeredByType = rental.endType || '';
  }

  // Parse distance
  let distanceKm = 0;
  let distanceMiles = 0;
  if (rental.distanceFormatted) {
    try {
      const parsed = JSON.parse(rental.distanceFormatted);
      distanceKm = parsed.kilometers || 0;
      distanceMiles = parsed.miles || 0;
    } catch {
      // Use raw distance if JSON parsing fails
      distanceKm = (rental.distance || 0) / 1000;
      distanceMiles = (rental.distance || 0) / 1609.34;
    }
  } else if (rental.tripDistance) {
    distanceKm = rental.tripDistance;
    distanceMiles = rental.tripDistance * 0.621371;
  }

  return {
    id: rental.id, // Use GraphQL UUID for routing and display
    gid: rental.id,
    old_id: rental.oldId,
    charge_id: rental.chargeId,
    startTime: rental.startTime,
    endTime: rental.endTime,
    duration: rental.duration,
    end_by: rental.endBy,
    chargeAmount: rental.chargeAmount,
    taxAmount: rental.taxAmount ? parseFloat(rental.taxAmount) : rental.taxAmount,
    rating: rental.rating,
    type: rental.hardware?.hardwareType?.name,

    // User info
    user: rental.user
      ? {
          full_name:
            rental.user.fullName || `${rental.user.firstName} ${rental.user.lastName}`,
          phone_number: rental.user.phone,
          first_name: rental.user.firstName,
          last_name: rental.user.lastName,
        }
      : { full_name: '', phone_number: '', first_name: '', last_name: '' },

    // Hardware info
    hardware: rental.hardware
      ? ({
          id: rental.hardware.id, // Use UUID for routing
          old_id: rental.hardware.oldId,
          number: rental.hardware.number,
          type_name: rental.hardware.hardwareType?.name,
          hardware_product_name: rental.hardware.hardwareProduct?.name,
          display_name: rental.hardware.label || `#${rental.hardware.number}`,
          has_gps: rental.hardware.hardwareProduct?.hasLocationTracking || false,
          children:
            rental.hardware.children?.map((child) => ({
              id: child.id, // Use UUID for routing
              old_id: child.oldId,
              number: child.number,
              type_name: child.hardwareType?.name,
              display_name: child.label || `#${child.number}`,
            })) || [],
        } as any)
      : ({} as any),
    hardwareNumber: rental.hardware?.number ?? null,

    // Station info
    station: rental.startLocation
      ? ({
          id: rental.startLocation.id, // Use UUID for routing
          name: rental.startLocation.name,
          longitude: rental.startLocation.lng,
          latitude: rental.startLocation.lat,
        } as any)
      : ({} as any),

    start: {
      station: rental.startLocation
        ? {
            id: rental.startLocation.id, // Use UUID for routing
            name: rental.startLocation.name,
            longitude: rental.startLocation.lng || 0,
            latitude: rental.startLocation.lat || 0,
          }
        : { id: '', name: '', longitude: 0, latitude: 0 },
      time: rental.startTime || '',
      triggered_by_type: startTriggeredByType,
    },

    end: rental.endTime
      ? {
          station: rental.endLocation
            ? {
                id: rental.endLocation.id, // Use UUID for routing
                name: rental.endLocation.name,
                longitude: rental.endLocation.lng,
                latitude: rental.endLocation.lat,
              }
            : null,
          time: rental.endTime,
          triggered_by_type: endTriggeredByType,
          image_url: rental.endImageUrl || undefined,
        }
      : null,

    // System info
    system: rental.system
      ? ({
          id: rental.system.oldId || rental.system.id,
          gid: rental.system.id,
          old_id: rental.system.oldId,
          name: rental.system.name,
          country: rental.system.countryCode,
          billing: rental.system.billingLive,
          round_trip: rental.system.roundTrip || false,
          new_hardware: rental.system.newHardware || false,
        } as any)
      : undefined,

    // Billing
    billing,

    // Grace period / force end
    ended_within_grace_period: rental.endedWithinGracePeriod || false,
    force_end:
      rental.forceEndType || rental.forceEndTime
        ? {
            type: rental.forceEndType,
            time: rental.forceEndTime,
          }
        : null,

    // Distance
    distance: {
      kilometers: distanceKm,
      miles: distanceMiles,
    },

    // Unlocks - not available in GraphQL, keep empty
    unlocks: [],

    // Support messages
    support_messages: rental.supportMessages
      ? mapRelaySupportMessages(
          rental.supportMessages as SupportMessageGraphQL[],
          rental.user?.fullName
        )
      : [],

    // Images
    images: rental.images as string[] | undefined,

    // Review status (null for legacy rentals with no review)
    review_status: rental.reviewStatus ?? null,
  };
}

// ============================================================================
// Rate Mapper
// ============================================================================

/**
 * Map GraphQL Rate to legacy IRate interface
 */
export function mapRelayRateToIRate(rate: RateGraphQL): IRate {
  return {
    id: rate.oldId || rate.id,
    gid: rate.id,
    old_id: rate.oldId || '',
    name: rate.name,
    base_rate: rate.baseRate || 0,
    price_per_period: rate.pricePerPeriod || 0,
    period_duration: rate.periodDuration || 0,
    free_time: rate.freeTime || 0,
    credit_card_hold: rate.creditCardHold || null,
    minimum_wallet_deposit: rate.minimumWalletDeposit || null,
    hidden: rate.hidden || false,
    membership_required: rate.membershipRequired || false,
    max_rentals: rate.maxRentals || null,
    max_periods: rate.maxPeriods || null,
    active_on: rate.activeOn || '',
    archived: rate.archivedOn || null,
    // Computed fields
    stringDuration: formatDuration(rate.periodDuration || 0),
    overview: buildRateOverview(rate),
    reservation: false, // Not available in GraphQL
  };
}

// ============================================================================
// Perk Mapper
// ============================================================================

/**
 * Map GraphQL Perk to legacy IPerk interface
 */
export function mapRelayPerkToIPerk(perk: PerkGraphQL): IPerk {
  return {
    id: perk.oldId || perk.id,
    membership_id: perk.membershipOldId || '',
    membership_name: perk.membership?.name || '',
    free_time: perk.freeTime || 0,
    money_off: perk.moneyOff || 0,
    discount: perk.discount || 0,
  };
}

// ============================================================================
// Coupon Mapper
// ============================================================================

/**
 * Map GraphQL Coupon to legacy ICoupon interface
 */
export function mapRelayCouponToICoupon(coupon: CouponGraphQL): ICoupon {
  return {
    id: coupon.oldId || coupon.id,
    old_id: coupon.oldId || '',
    code: coupon.code || '',
    name: coupon.name,
    type: coupon.discountType || '',
    discount: coupon.discount || 0,
    duration: coupon.duration || 0,
    uses: coupon.uses || 0,
    active: coupon.active || false,
    created: coupon.created || '',
  };
}

// ============================================================================
// Fee Mapper
// ============================================================================

/**
 * Map GraphQL Fee to legacy IFee interface
 */
export function mapRelayFeeToIFee(fee: FeeGraphQL): IFee {
  return {
    id: fee.id,
    name: fee.name,
    amount: fee.amount,
    applied_post_tax: fee.appliedPostTax,
  };
}

/**
 * Map array of GraphQL fees to IFee array
 */
export function mapRelayFeesToIFees(fees: FeeGraphQL[]): IFee[] {
  return fees.map(mapRelayFeeToIFee);
}

// ============================================================================
// Pending Charge Mapper
// ============================================================================

/**
 * Map rental pending charge fields to IPendingCharge
 */
function mapRelayPendingCharge(rental: RentalDetailGraphQL): IPendingCharge {
  return {
    pending: rental.chargePending || false,
    note: rental.chargePendingNote || '',
    decision_date: rental.chargePendingDecisionDate || '',
    // Preserve null when there is no decision. Coercing to `false` would make
    // the UI treat a rental with no pending charge as a *declined* charge and
    // zero out its total (see transactionInformation.tsx `pendingChargeDeclined`).
    status: rental.chargePendingStatus ?? null,
  };
}

// ============================================================================
// Support Message Mapper
// ============================================================================

/**
 * Map GraphQL support messages to IRentalSupport array
 */
export function mapRelaySupportMessages(
  messages: SupportMessageGraphQL[],
  userName?: string | null
): IRentalSupport[] {
  return messages.map((msg) => ({
    id: msg.id,
    old_id: msg.oldId || '',
    content: msg.content || '',
    created_on: msg.created || '',
    full_name: userName || '',
    state: msg.state ?? null,
  }));
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format duration in seconds to human-readable string
 */
function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} seconds`;
  }
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  const hours = Math.floor(seconds / 3600);
  const remainingMinutes = Math.floor((seconds % 3600) / 60);
  if (remainingMinutes === 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }
  return `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMinutes} minute${
    remainingMinutes !== 1 ? 's' : ''
  }`;
}

/**
 * Build a rate overview string
 */
function buildRateOverview(rate: RateGraphQL): string {
  const parts: string[] = [];

  if (rate.baseRate && rate.baseRate > 0) {
    parts.push(`$${(rate.baseRate / 100).toFixed(2)} base`);
  }

  if (rate.pricePerPeriod && rate.pricePerPeriod > 0 && rate.periodDuration) {
    const durationStr = formatDuration(rate.periodDuration);
    parts.push(`$${(rate.pricePerPeriod / 100).toFixed(2)} per ${durationStr}`);
  }

  if (rate.freeTime && rate.freeTime > 0) {
    parts.push(`${formatDuration(rate.freeTime)} free`);
  }

  return parts.join(', ');
}
